/**
 * AdminCleanupTools — owner-only mock-ads cleanup page.
 *
 * Policy v2_seed_only (iter264):
 *   • DELETABLE bucket = ads whose user_id starts with the literal prefix
 *     "seed-user-". Only these are touched by the execute path.
 *   • SUSPICIOUS bucket = broader-classifier candidates that are NOT
 *     seed-user-* (UUID owners, admin-owned, no-owner, title-prefix,
 *     source=seed flags on non-seed users, etc.). These are surfaced for
 *     manual review and are NEVER deleted by this tool.
 *
 * UI behaviour:
 *   1. "Dry run" → GET /api/admin/cleanup/mock-ads/dry-run, shows
 *      deletable_count, suspicious_count, real_count, deletable owners,
 *      deletable sample (20), and the FULL suspicious_list.
 *   2. "Delete seed-user-* ads" stays disabled until:
 *        • a dry run has been executed,
 *        • deletable_count > 0,
 *        • the user typed the exact phrase "DELETE MOCK ADS".
 *      The suspicious bucket is shown but does NOT block the button
 *      because those ads are excluded server-side (refused with 409 if
 *      any non-seed slips in).
 *   3. POST /api/admin/cleanup/mock-ads/execute with the typed phrase.
 *   4. Post-delete result shows deleted_count, remaining_seed_user_ads,
 *      and remaining_suspicious_ads.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { toast } from 'sonner';
import {
  AlertTriangle, ShieldCheck, Trash2, ListChecks, ArrowLeft, Eye,
} from 'lucide-react';
import { getApiBase } from '../../api/httpClient';
import { removeAdsFromAllRecentlyViewed } from '../../utils/recentlyViewed';
import './AdminLayout.css';

const OWNER_EMAIL = 'wassla.connect@gmail.com';
const CONFIRM_PHRASE = 'DELETE MOCK ADS';
const SUSPICIOUS_CONFIRM_PHRASE = 'REMOVE SUSPICIOUS ADS';
const PRELAUNCH_RESET_CONFIRM = 'RESET ALL PRELAUNCH ADS';

export default function AdminCleanupTools() {
  const { user, token } = useAuth();
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const isArabic = lang === 'ar';

  const [dryRunResult, setDryRunResult] = useState(null);
  const [dryRunLoading, setDryRunLoading] = useState(false);
  const [executeResult, setExecuteResult] = useState(null);
  const [executeLoading, setExecuteLoading] = useState(false);
  const [confirmInput, setConfirmInput] = useState('');

  // iter265 — manual-review suspicious removal flow.
  const [selectedSuspicious, setSelectedSuspicious] = useState(() => new Set());
  const [suspiciousConfirmInput, setSuspiciousConfirmInput] = useState('');
  const [suspiciousLoading, setSuspiciousLoading] = useState(false);
  const [suspiciousResult, setSuspiciousResult] = useState(null);

  // iter266 — pre-launch FULL ads reset.
  const [prelaunchDryRun, setPrelaunchDryRun] = useState(null);
  const [prelaunchDryRunLoading, setPrelaunchDryRunLoading] = useState(false);
  const [prelaunchConfirmInput, setPrelaunchConfirmInput] = useState('');
  const [prelaunchExecuteLoading, setPrelaunchExecuteLoading] = useState(false);
  const [prelaunchResult, setPrelaunchResult] = useState(null);

  const isOwner = (
    user?.is_admin &&
    (user?.email || '').trim().toLowerCase() === OWNER_EMAIL
  );

  useEffect(() => {
    if (user && !isOwner) {
      toast.error(
        isArabic
          ? 'هذه الأداة متاحة للمالك فقط.'
          : 'This tool is restricted to the owner admin.'
      );
      navigate('/admin/overview', { replace: true });
    }
  }, [user, isOwner, navigate, isArabic]);

  if (!user) {
    return (
      <div className="admin-shell">
        <div className="admin-content">
          {isArabic ? 'جارٍ التحقق…' : 'Verifying access…'}
        </div>
      </div>
    );
  }

  if (!isOwner) return null;

  const API = getApiBase();
  const rawToken = token || (typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null);
  const liveToken =
    rawToken && rawToken !== 'null' && rawToken !== 'undefined'
      ? String(rawToken).trim()
      : null;
  const authHeader = liveToken ? { 'Authorization': `Bearer ${liveToken}` } : {};

  const runDryRun = async () => {
    if (!liveToken) {
      toast.error(isArabic ? 'انتهت الجلسة. سجّل الدخول مرة أخرى.' : 'Session expired. Please sign in again.');
      navigate('/auth');
      return;
    }
    setDryRunLoading(true);
    setExecuteResult(null);
    setSuspiciousResult(null);
    setSelectedSuspicious(new Set());
    setSuspiciousConfirmInput('');
    try {
      const r = await fetch(`${API}/api/admin/cleanup/mock-ads/dry-run`, {
        headers: authHeader,
        credentials: 'include',
      });
      const data = await r.json();
      if (!r.ok) {
        throw new Error(data?.detail || `HTTP ${r.status}`);
      }
      setDryRunResult(data);
      toast.success(
        isArabic
          ? `تم الفحص: ${data.deletable_count} قابل للحذف، ${data.suspicious_count} مشبوه (لن يُحذف).`
          : `Dry-run complete: ${data.deletable_count} deletable, ${data.suspicious_count} suspicious (will NOT be deleted).`
      );
    } catch (e) {
      toast.error(e.message || 'Dry-run failed');
    } finally {
      setDryRunLoading(false);
    }
  };

  const deletableCount = dryRunResult?.deletable_count ?? 0;
  const suspiciousCount = dryRunResult?.suspicious_count ?? 0;
  const canExecute = !!dryRunResult
    && deletableCount > 0
    && confirmInput === CONFIRM_PHRASE
    && !executeLoading;

  const runExecute = async () => {
    if (!canExecute) return;
    if (!liveToken) {
      toast.error(isArabic ? 'انتهت الجلسة.' : 'Session expired.');
      navigate('/auth');
      return;
    }
    setExecuteLoading(true);
    try {
      const r = await fetch(`${API}/api/admin/cleanup/mock-ads/execute`, {
        method: 'POST',
        headers: { ...authHeader, 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ confirm: CONFIRM_PHRASE }),
      });
      const data = await r.json();
      if (!r.ok) {
        const msg = (data && (data.detail?.message || data.detail)) || `HTTP ${r.status}`;
        throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg));
      }
      setExecuteResult(data);
      setConfirmInput('');
      runDryRun();
      toast.success(
        isArabic
          ? `تم الحذف. تم حذف ${data.deleted_count} إعلان.`
          : `Deleted ${data.deleted_count} seed-user-* ads.`
      );
    } catch (e) {
      toast.error(e.message || 'Delete failed');
    } finally {
      setExecuteLoading(false);
    }
  };

  // ── iter265: manual-review suspicious-ads removal ────────────────────
  const suspiciousList = dryRunResult?.suspicious_list || [];

  const toggleSuspicious = (adId) => {
    setSelectedSuspicious((prev) => {
      const next = new Set(prev);
      if (next.has(adId)) next.delete(adId);
      else next.add(adId);
      return next;
    });
  };

  const selectAllSuspicious = () => {
    setSelectedSuspicious(
      new Set(suspiciousList.map((s) => s.ad_id).filter(Boolean))
    );
  };

  const clearSuspiciousSelection = () => {
    setSelectedSuspicious(new Set());
  };

  const selectedSuspiciousCount = selectedSuspicious.size;
  const canRemoveSuspicious =
    selectedSuspiciousCount > 0 &&
    suspiciousConfirmInput === SUSPICIOUS_CONFIRM_PHRASE &&
    !suspiciousLoading;

  const runRemoveSuspicious = async () => {
    if (!canRemoveSuspicious) return;
    if (!liveToken) {
      toast.error(isArabic ? 'انتهت الجلسة.' : 'Session expired.');
      navigate('/auth');
      return;
    }
    const adIds = Array.from(selectedSuspicious);
    setSuspiciousLoading(true);
    try {
      const r = await fetch(`${API}/api/admin/cleanup/suspicious-ads/execute`, {
        method: 'POST',
        headers: { ...authHeader, 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          confirm: SUSPICIOUS_CONFIRM_PHRASE,
          ad_ids: adIds,
        }),
      });
      const data = await r.json();
      if (!r.ok) {
        // Server returns a structured 409 when any ad_id fails server-side
        // re-validation. Surface the rejected ids so the admin knows which
        // rows to deselect / refresh.
        const detail = data?.detail;
        if (r.status === 409 && detail && Array.isArray(detail.rejected)) {
          const sample = detail.rejected
            .slice(0, 3)
            .map((x) => `${x.ad_id}: ${x.reason}`)
            .join(' • ');
          throw new Error(
            isArabic
              ? `الخادم رفض الدفعة (${detail.rejected.length} عنصر). أعد تشغيل الفحص. مثال: ${sample}`
              : `Server rejected batch (${detail.rejected.length} item${detail.rejected.length === 1 ? '' : 's'}). Re-run dry-run. Example: ${sample}`
          );
        }
        const msg = (detail?.message || detail) || `HTTP ${r.status}`;
        throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg));
      }
      setSuspiciousResult(data);
      // Scrub the deleted ad_ids from the on-device "Recently Viewed"
      // buckets so the carousel / Home / Shop don't fetch zombie ids.
      try {
        const removed = removeAdsFromAllRecentlyViewed(
          data.deleted_ad_ids || adIds
        );
        if (removed > 0) {
          // best-effort: silent. No toast — admin already has the result card.
        }
      } catch (_e) {
        /* localStorage unavailable — ignore */
      }
      setSelectedSuspicious(new Set());
      setSuspiciousConfirmInput('');
      // Re-run dry-run so the counts refresh automatically.
      runDryRun();
      toast.success(
        isArabic
          ? `تم. تم حذف ${data.deleted_count} إعلان مشبوه.`
          : `Removed ${data.deleted_count} suspicious test ad${data.deleted_count === 1 ? '' : 's'}.`
      );
    } catch (e) {
      toast.error(e.message || 'Removal failed');
    } finally {
      setSuspiciousLoading(false);
    }
  };

  // ── iter266: pre-launch FULL ads reset ───────────────────────────────
  const runPrelaunchDryRun = async () => {
    if (!liveToken) {
      toast.error(isArabic ? 'انتهت الجلسة.' : 'Session expired.');
      navigate('/auth');
      return;
    }
    setPrelaunchDryRunLoading(true);
    setPrelaunchResult(null);
    try {
      const r = await fetch(`${API}/api/admin/cleanup/all-ads/dry-run`, {
        headers: authHeader,
        credentials: 'include',
      });
      const data = await r.json();
      if (!r.ok) {
        throw new Error(data?.detail || `HTTP ${r.status}`);
      }
      setPrelaunchDryRun(data);
      toast.success(
        isArabic
          ? `الفحص: سيتم حذف ${data.total_ads} إعلان.`
          : `Dry run: ${data.total_ads} ads would be deleted.`
      );
    } catch (e) {
      toast.error(e.message || 'Dry-run failed');
    } finally {
      setPrelaunchDryRunLoading(false);
    }
  };

  const canPrelaunchExecute =
    !!prelaunchDryRun &&
    prelaunchDryRun.total_ads > 0 &&
    prelaunchConfirmInput === PRELAUNCH_RESET_CONFIRM &&
    !prelaunchExecuteLoading;

  const runPrelaunchExecute = async () => {
    if (!canPrelaunchExecute) return;
    if (!liveToken) {
      toast.error(isArabic ? 'انتهت الجلسة.' : 'Session expired.');
      navigate('/auth');
      return;
    }
    setPrelaunchExecuteLoading(true);
    try {
      const r = await fetch(`${API}/api/admin/cleanup/all-ads/execute`, {
        method: 'POST',
        headers: { ...authHeader, 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ confirm: PRELAUNCH_RESET_CONFIRM }),
      });
      const data = await r.json();
      if (!r.ok) {
        const msg = (data?.detail?.message || data?.detail) || `HTTP ${r.status}`;
        throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg));
      }
      setPrelaunchResult(data);
      // Best-effort: wipe every Recently-Viewed bucket on this device so
      // the strip cannot show zombie ids after a full reset.
      try {
        if (Array.isArray(prelaunchDryRun?.sample_20_ads)) {
          // We only have the sample 20 ad_ids client-side. The carousel
          // ALSO server-validates via /api/ads/by-ids so deleted ids
          // simply return no items — no broken cards. Still, scrub the
          // sample so localStorage usage shrinks immediately.
          const adIds = prelaunchDryRun.sample_20_ads
            .map((s) => s.ad_id)
            .filter(Boolean);
          removeAdsFromAllRecentlyViewed(adIds);
        }
      } catch (_e) {
        /* localStorage unavailable — ignore */
      }
      setPrelaunchConfirmInput('');
      // Re-run dry-run so counts refresh to 0 in the UI.
      runPrelaunchDryRun();
      // Also refresh the seed-cleanup dry-run so its counters reflect 0.
      runDryRun();
      toast.success(
        isArabic
          ? `تم. تم حذف ${data.deleted_count} إعلان.`
          : `Reset complete. ${data.deleted_count} ads deleted.`
      );
    } catch (e) {
      toast.error(e.message || 'Reset failed');
    } finally {
      setPrelaunchExecuteLoading(false);
    }
  };

  return (
    <div className="admin-shell">
      <div className="admin-content" data-testid="admin-cleanup-tools">
        <div className="admin-back-row">
          <button
            type="button"
            className="admin-btn"
            onClick={() => navigate('/admin/overview')}
            data-testid="cleanup-back-btn"
          >
            <ArrowLeft size={14} />
            {isArabic ? 'رجوع' : 'Back'}
          </button>
        </div>

        <h1 className="admin-page-title">
          <ShieldCheck size={18} style={{ verticalAlign: 'middle', marginInlineEnd: 8 }} />
          {isArabic ? 'أدوات تنظيف المسؤول' : 'Admin Cleanup Tools'}
        </h1>
        <p className="admin-page-subtitle">
          {isArabic
            ? 'يحذف هذا التنظيف فقط الإعلانات المملوكة لحسابات seed-user-* في قاعدة بيانات الإنتاج. باقي المرشحين (UUID، حسابات حقيقية، حسابات مسؤولين، بدون مالك، عناوين تجريبية) يُعرض للمراجعة فقط ولا يُحذف.'
            : 'This tool deletes ONLY ads whose owner user_id starts with "seed-user-". All other candidates (UUID owners, real-user owned, admin-owned, no-owner, title-prefix only) are shown for manual review and are NEVER deleted by this tool.'}
        </p>

        {/* 1. DRY RUN CARD */}
        <div className="admin-card">
          <h3 className="admin-card-title">
            <ListChecks size={14} style={{ verticalAlign: 'middle', marginInlineEnd: 6 }} />
            {isArabic ? '1. فحص تجريبي (بدون حذف)' : '1. Dry run (no deletion)'}
          </h3>
          <p style={{ fontSize: 13, color: 'var(--admin-text-dim)' }}>
            {isArabic
              ? 'سياسة v2_seed_only: يُحذف فقط الإعلان الذي يبدأ user_id الخاص به بـ "seed-user-". أي مرشّح آخر يُدرج في مجموعة المراجعة اليدوية ولن يتأثر.'
              : 'Policy v2_seed_only: only ads whose user_id starts with "seed-user-" are deletable. Every other candidate (UUIDs, real users, admins, no-owner, title-prefix) is surfaced for manual review only.'}
          </p>
          <button
            type="button"
            className="admin-btn primary"
            onClick={runDryRun}
            disabled={dryRunLoading}
            data-testid="cleanup-dry-run-btn"
          >
            {dryRunLoading
              ? (isArabic ? 'جارٍ الفحص…' : 'Running…')
              : (isArabic ? 'تشغيل فحص الإعلانات الوهمية' : 'Dry run mock ad cleanup')}
          </button>
        </div>

        {/* 2. DRY RUN RESULT */}
        {dryRunResult && (
          <div className="admin-card" data-testid="cleanup-dryrun-result">
            <h3 className="admin-card-title">
              {isArabic ? 'نتائج الفحص' : 'Dry run results'}
              {dryRunResult.policy_version && (
                <span
                  className="admin-pill blue"
                  style={{ marginInlineStart: 8, fontSize: 11 }}
                  data-testid="cleanup-policy-version"
                >
                  {dryRunResult.policy_version}
                </span>
              )}
            </h3>

            <div className="admin-grid" style={{ gap: 12 }}>
              <div className="admin-stat">
                <span className="admin-stat-value" data-testid="cleanup-total-ads">
                  {dryRunResult.total_ads}
                </span>
                <span className="admin-stat-label">{isArabic ? 'مجموع الإعلانات' : 'Total ads'}</span>
              </div>
              <div className="admin-stat">
                <span className="admin-stat-value" style={{ color: '#f59e0b' }} data-testid="cleanup-deletable-count">
                  {deletableCount}
                </span>
                <span className="admin-stat-label">
                  {isArabic ? 'قابل للحذف (seed-user-* فقط)' : 'Deletable (seed-user-* only)'}
                </span>
              </div>
              <div className="admin-stat">
                <span className="admin-stat-value" style={{ color: '#94a3b8' }} data-testid="cleanup-suspicious-count">
                  {suspiciousCount}
                </span>
                <span className="admin-stat-label">
                  {isArabic ? 'مشبوه — مراجعة يدوية (لن يُحذف)' : 'Suspicious — manual review (NOT deleted)'}
                </span>
              </div>
              <div className="admin-stat">
                <span className="admin-stat-value" style={{ color: '#22c55e' }} data-testid="cleanup-real-count">
                  {dryRunResult.real_count}
                </span>
                <span className="admin-stat-label">{isArabic ? 'إعلانات حقيقية' : 'Real-user ads kept'}</span>
              </div>
            </div>

            {/* Deletable owners — should all be seed-user-* */}
            <h4 style={{ marginTop: 14, fontSize: 13 }}>
              {isArabic ? 'مالكو المجموعة القابلة للحذف (يجب أن تبدأ كلها بـ seed-user-)' : 'Deletable owners (all must start with seed-user-)'}
            </h4>
            <pre className="admin-pre" data-testid="cleanup-deletable-owners">
{(dryRunResult.deletable_owners_top20 || []).map(([uid, c]) => `  ${String(uid).padEnd(30)} ${String(c).padStart(4)} ads`).join('\n') || '  (none)'}
            </pre>

            {/* Deletable sample */}
            <h4 style={{ marginTop: 14, fontSize: 13 }}>
              {isArabic
                ? `عينة من الإعلانات القابلة للحذف (${Math.min(20, deletableCount)} من ${deletableCount})`
                : `Deletable sample (${Math.min(20, deletableCount)} of ${deletableCount})`}
            </h4>
            <div style={{ overflowX: 'auto' }}>
              <table className="admin-table" data-testid="cleanup-deletable-sample">
                <thead>
                  <tr>
                    <th>ad_id</th>
                    <th>{isArabic ? 'العنوان' : 'Title'}</th>
                    <th>{isArabic ? 'الفئة' : 'Category'}</th>
                    <th>{isArabic ? 'الحالة' : 'Status'}</th>
                    <th>{isArabic ? 'المالك' : 'Owner'}</th>
                  </tr>
                </thead>
                <tbody>
                  {(dryRunResult.deletable_sample || []).map((s) => (
                    <tr key={s.ad_id}>
                      <td style={{ fontFamily: 'monospace', fontSize: 11 }}>{s.ad_id}</td>
                      <td>{s.title}</td>
                      <td>{s.category || '-'}</td>
                      <td>{s.status || '-'}</td>
                      <td style={{ fontFamily: 'monospace', fontSize: 11 }}>{s.user_id || '<none>'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Suspicious list — FULL, manual review only */}
            {suspiciousCount > 0 && (
              <div
                className="admin-card"
                style={{ marginTop: 14, borderInlineStart: '3px solid #f59e0b' }}
                data-testid="cleanup-suspicious-card"
              >
                <h4 style={{ color: '#f59e0b' }}>
                  <Eye size={14} style={{ verticalAlign: 'middle', marginInlineEnd: 6 }} />
                  {isArabic
                    ? `مشبوه — مراجعة يدوية (${suspiciousCount})`
                    : `Suspicious — manual review (${suspiciousCount})`}
                </h4>
                <p style={{ fontSize: 12, color: 'var(--admin-text-dim)' }}>
                  {isArabic
                    ? 'هذه الإعلانات لا يبدأ user_id الخاص بها بـ "seed-user-" ولكنها تطابق إشارة وهمية. راجع كل صف، ثم اختره فقط إذا تأكدت من أنه إعلان تجريبي/داخلي. لن يُحذف أي صف غير محدد.'
                    : 'These ads do NOT have a seed-user-* owner but match another mock signal. Review each row, then check only the rows you’re sure are test/mock/internal. Unchecked rows are never touched.'}
                </p>

                <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    className="admin-btn"
                    onClick={selectAllSuspicious}
                    data-testid="suspicious-select-all-btn"
                  >
                    {isArabic ? 'تحديد الكل' : 'Select all'}
                  </button>
                  <button
                    type="button"
                    className="admin-btn"
                    onClick={clearSuspiciousSelection}
                    disabled={selectedSuspiciousCount === 0}
                    data-testid="suspicious-clear-selection-btn"
                  >
                    {isArabic ? 'إلغاء التحديد' : 'Clear selection'}
                  </button>
                  <span
                    className="admin-pill blue"
                    style={{ alignSelf: 'center' }}
                    data-testid="suspicious-selected-count"
                  >
                    {isArabic
                      ? `محدد: ${selectedSuspiciousCount} من ${suspiciousCount}`
                      : `Selected: ${selectedSuspiciousCount} of ${suspiciousCount}`}
                  </span>
                </div>

                <div style={{ overflowX: 'auto' }}>
                  <table className="admin-table" data-testid="cleanup-suspicious-table">
                    <thead>
                      <tr>
                        <th style={{ width: 32 }} aria-label="select" />
                        <th>ad_id</th>
                        <th>{isArabic ? 'العنوان' : 'Title'}</th>
                        <th>{isArabic ? 'الفئة' : 'Category'}</th>
                        <th>{isArabic ? 'الحالة' : 'Status'}</th>
                        <th>{isArabic ? 'المالك' : 'Owner'}</th>
                        <th>{isArabic ? 'تاريخ الإنشاء' : 'Created'}</th>
                        <th>{isArabic ? 'السبب' : 'Reason'}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {suspiciousList.map((s) => {
                        const checked = selectedSuspicious.has(s.ad_id);
                        return (
                          <tr key={s.ad_id} className={checked ? 'admin-row-selected' : undefined}>
                            <td>
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleSuspicious(s.ad_id)}
                                data-testid={`suspicious-row-checkbox-${s.ad_id}`}
                                aria-label={`Select ${s.ad_id}`}
                              />
                            </td>
                            <td style={{ fontFamily: 'monospace', fontSize: 11 }}>{s.ad_id}</td>
                            <td>{s.title}</td>
                            <td>{s.category || '-'}</td>
                            <td>{s.status || '-'}</td>
                            <td style={{ fontFamily: 'monospace', fontSize: 11 }}>{s.user_id || '<none>'}</td>
                            <td style={{ fontSize: 11 }}>
                              {s.created_at ? String(s.created_at).slice(0, 19).replace('T', ' ') : '-'}
                            </td>
                            <td style={{ fontSize: 11 }}>{s.reason}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <p style={{ marginTop: 10, fontSize: 12, color: 'var(--admin-text-dim)' }}>
              {isArabic
                ? `النسخة الاحتياطية ستُحفظ في مجموعة: ${dryRunResult.backup_collection_on_execute || 'ads_mock_backups'}`
                : `Backup collection on execute: ${dryRunResult.backup_collection_on_execute || 'ads_mock_backups'}`}
            </p>
          </div>
        )}

        {/* 3. EXECUTE CARD */}
        {dryRunResult && (
          <div className="admin-card" data-testid="cleanup-execute-card">
            <h3 className="admin-card-title" style={{ color: '#f87171' }}>
              <Trash2 size={14} style={{ verticalAlign: 'middle', marginInlineEnd: 6 }} />
              {isArabic ? '2. حذف الإعلانات (seed-user-* فقط)' : '2. Delete ads (seed-user-* only)'}
            </h3>

            {/* Excluded-from-deletion banner — shown whenever there ARE
                suspicious ads, regardless of whether we have deletable ones.
                Pure informational: deletion never touches these. */}
            {suspiciousCount > 0 && (
              <div
                className="admin-pill yellow"
                style={{ marginBottom: 10, display: 'inline-flex', alignItems: 'center', gap: 6 }}
                data-testid="cleanup-excluded-banner"
              >
                <AlertTriangle size={12} />
                {isArabic
                  ? `مستبعد من الحذف: ${suspiciousCount}`
                  : `Excluded from deletion: ${suspiciousCount}`}
              </div>
            )}

            {deletableCount === 0 ? (
              <p style={{ color: '#22c55e' }} data-testid="cleanup-nothing-to-delete">
                {isArabic
                  ? '✓ لا يوجد إعلانات seed-user-* للحذف.'
                  : '✓ No seed-user-* ads to delete.'}
              </p>
            ) : (
              <>
                <p style={{ fontSize: 13 }}>
                  {isArabic
                    ? `سيتم حذف ${deletableCount} إعلان من حسابات seed-user-* فقط بشكل دائم. لن يُحذف ${suspiciousCount} من المجموعة المشبوهة. سيتم حفظ نسخة احتياطية أولاً.`
                    : `${deletableCount} seed-user-* ads will be permanently deleted. ${suspiciousCount} suspicious ads will be SKIPPED. A backup is written first.`}
                </p>
                <label style={{ display: 'block', fontSize: 12, marginTop: 10 }}>
                  {isArabic
                    ? `اكتب "${CONFIRM_PHRASE}" للتأكيد:`
                    : `Type "${CONFIRM_PHRASE}" to confirm:`}
                </label>
                <input
                  type="text"
                  value={confirmInput}
                  onChange={(e) => setConfirmInput(e.target.value)}
                  placeholder={CONFIRM_PHRASE}
                  className="admin-input"
                  data-testid="cleanup-confirm-input"
                  autoComplete="off"
                  spellCheck="false"
                  style={{ marginTop: 4 }}
                />
                <button
                  type="button"
                  className="admin-btn danger"
                  style={{ marginTop: 10 }}
                  onClick={runExecute}
                  disabled={!canExecute}
                  data-testid="cleanup-execute-btn"
                >
                  {executeLoading
                    ? (isArabic ? 'جارٍ الحذف…' : 'Deleting…')
                    : (isArabic
                        ? `حذف ${deletableCount} إعلان seed-user-*`
                        : `Delete ${deletableCount} seed-user-* ads`)}
                </button>
              </>
            )}
          </div>
        )}

        {/* 4. EXECUTE RESULT */}
        {executeResult && (
          <div
            className="admin-card"
            data-testid="cleanup-execute-result"
            style={{ borderInlineStart: '3px solid #22c55e' }}
          >
            <h3 className="admin-card-title" style={{ color: '#22c55e' }}>
              {isArabic ? 'تم بنجاح' : 'Done'}
            </h3>
            <ul style={{ fontSize: 13, lineHeight: 1.8, margin: 0, paddingInlineStart: 18 }}>
              <li>
                {isArabic ? 'الإعلانات المحذوفة' : 'Deleted'}:
                {' '}<strong>{executeResult.deleted_count}</strong>
              </li>
              <li>
                {isArabic ? 'إجمالي الإعلانات المتبقية' : 'Remaining total ads'}:
                {' '}<strong>{executeResult.remaining_total_ads}</strong>
              </li>
              <li>
                {isArabic ? 'إعلانات seed-user-* المتبقية' : 'Remaining seed-user-* ads'}:
                {' '}<strong>{executeResult.remaining_seed_user_ads ?? executeResult.remaining_mock_candidates}</strong>
              </li>
              {typeof executeResult.remaining_suspicious_ads === 'number' && (
                <li>
                  {isArabic ? 'إعلانات مشبوهة متبقية (لم تُمسّ)' : 'Remaining suspicious ads (untouched)'}:
                  {' '}<strong>{executeResult.remaining_suspicious_ads}</strong>
                </li>
              )}
              <li>
                {isArabic ? 'معرّف النسخة الاحتياطية' : 'Backup batch id'}:
                {' '}<code>{executeResult.backup_batch_id}</code>
              </li>
              <li>
                {isArabic ? 'النسخة الاحتياطية محفوظة في' : 'Backup collection'}:
                {' '}<code>{executeResult.backup_collection}</code>
              </li>
              <li>
                {isArabic ? 'المسؤول لا يزال مسجلاً' : 'Admin still logged in'}:
                {' '}<strong>{executeResult.admin_still_logged_in ? '✅' : '❌'}</strong>
              </li>
              {executeResult.policy_version && (
                <li>
                  {isArabic ? 'إصدار السياسة' : 'Policy version'}:
                  {' '}<code>{executeResult.policy_version}</code>
                </li>
              )}
            </ul>
          </div>
        )}

        {/* 5. SUSPICIOUS MANUAL-REVIEW CARD (iter265) */}
        {dryRunResult && suspiciousCount > 0 && (
          <div className="admin-card" data-testid="cleanup-suspicious-execute-card">
            <h3 className="admin-card-title" style={{ color: '#f59e0b' }}>
              <Eye size={14} style={{ verticalAlign: 'middle', marginInlineEnd: 6 }} />
              {isArabic
                ? '3. إزالة الإعلانات المشبوهة (مراجعة يدوية)'
                : '3. Remove suspicious test ads (manual review)'}
            </h3>
            <p style={{ fontSize: 13 }}>
              {isArabic
                ? 'لا يحذف هذا الإجراء أي صف ما لم تحدد مربعه أعلاه. ستتم إعادة التحقق من كل ad_id على الخادم — وأي صف لم يعد مشبوهاً يرفض الدفعة بأكملها (HTTP 409) ولن تتم أي عمليات حذف.'
                : 'This action only removes rows you explicitly checked above. Every ad_id is re-validated server-side — any row that is no longer suspicious causes the entire batch to abort (HTTP 409) with no deletions.'}
            </p>
            <ul style={{ fontSize: 12, color: 'var(--admin-text-dim)', marginTop: 6 }}>
              <li>{isArabic ? '✓ نسخة احتياطية في ads_mock_backups قبل الحذف' : '✓ Backup to ads_mock_backups before deletion'}</li>
              <li>{isArabic ? '✓ سجل تدقيق منفصل بإجراء suspicious_ads_cleanup' : '✓ Separate audit log entry (action=suspicious_ads_cleanup)'}</li>
              <li>{isArabic ? '✓ لا يلمس المستخدمين أو المسؤولين أو الإعلانات الحقيقية أو إعدادات النظام' : '✓ Never touches users, admins, real ads, .env, or system keys'}</li>
              <li>{isArabic ? '✓ يمسح ad_ids المحذوفة من ذاكرة "المشاهدات الأخيرة" المحلية' : '✓ Scrubs deleted ad_ids from local Recently-Viewed cache on this device'}</li>
            </ul>

            {selectedSuspiciousCount === 0 ? (
              <p
                style={{ color: 'var(--admin-text-dim)', fontSize: 13, marginTop: 10 }}
                data-testid="suspicious-nothing-selected"
              >
                {isArabic
                  ? 'لم يتم تحديد أي صف بعد. حدّد المربعات في جدول المشبوه أعلاه أولاً.'
                  : 'No rows selected yet. Check boxes in the suspicious table above first.'}
              </p>
            ) : (
              <>
                <p style={{ fontSize: 13, marginTop: 10 }}>
                  {isArabic
                    ? `سيتم حذف ${selectedSuspiciousCount} إعلان محدد بشكل دائم. سيتم حفظ نسخة احتياطية أولاً.`
                    : `${selectedSuspiciousCount} selected ad${selectedSuspiciousCount === 1 ? '' : 's'} will be permanently removed. A backup is written first.`}
                </p>
                <label style={{ display: 'block', fontSize: 12, marginTop: 8 }}>
                  {isArabic
                    ? `اكتب "${SUSPICIOUS_CONFIRM_PHRASE}" للتأكيد:`
                    : `Type "${SUSPICIOUS_CONFIRM_PHRASE}" to confirm:`}
                </label>
                <input
                  type="text"
                  value={suspiciousConfirmInput}
                  onChange={(e) => setSuspiciousConfirmInput(e.target.value)}
                  placeholder={SUSPICIOUS_CONFIRM_PHRASE}
                  className="admin-input"
                  data-testid="suspicious-confirm-input"
                  autoComplete="off"
                  spellCheck="false"
                  style={{ marginTop: 4 }}
                />
                <button
                  type="button"
                  className="admin-btn danger"
                  style={{ marginTop: 10 }}
                  onClick={runRemoveSuspicious}
                  disabled={!canRemoveSuspicious}
                  data-testid="suspicious-execute-btn"
                >
                  {suspiciousLoading
                    ? (isArabic ? 'جارٍ الإزالة…' : 'Removing…')
                    : (isArabic
                        ? `إزالة ${selectedSuspiciousCount} إعلان مشبوه محدد`
                        : `Remove ${selectedSuspiciousCount} selected suspicious ad${selectedSuspiciousCount === 1 ? '' : 's'}`)}
                </button>
              </>
            )}
          </div>
        )}

        {/* 6. SUSPICIOUS EXECUTE RESULT */}
        {suspiciousResult && (
          <div
            className="admin-card"
            data-testid="suspicious-execute-result"
            style={{ borderInlineStart: '3px solid #22c55e' }}
          >
            <h3 className="admin-card-title" style={{ color: '#22c55e' }}>
              {isArabic ? 'تمت إزالة الإعلانات المشبوهة' : 'Suspicious ads removed'}
            </h3>
            <ul style={{ fontSize: 13, lineHeight: 1.8, margin: 0, paddingInlineStart: 18 }}>
              <li>
                {isArabic ? 'الإعلانات المحذوفة' : 'Deleted'}:
                {' '}<strong data-testid="suspicious-result-deleted">{suspiciousResult.deleted_count}</strong>
              </li>
              <li>
                {isArabic ? 'إجمالي الإعلانات المتبقية' : 'Remaining total ads'}:
                {' '}<strong data-testid="suspicious-result-remaining-total">{suspiciousResult.remaining_total_ads}</strong>
              </li>
              <li>
                {isArabic ? 'إعلانات seed-user-* المتبقية' : 'Remaining seed-user-* ads'}:
                {' '}<strong>{suspiciousResult.remaining_seed_user_ads}</strong>
              </li>
              <li>
                {isArabic ? 'إعلانات مشبوهة متبقية' : 'Remaining suspicious ads'}:
                {' '}<strong data-testid="suspicious-result-remaining-suspicious">{suspiciousResult.remaining_suspicious_ads}</strong>
              </li>
              <li>
                {isArabic ? 'معرّف النسخة الاحتياطية' : 'Backup batch id'}:
                {' '}<code>{suspiciousResult.backup_batch_id}</code>
              </li>
              <li>
                {isArabic ? 'النسخة الاحتياطية محفوظة في' : 'Backup collection'}:
                {' '}<code>{suspiciousResult.backup_collection}</code>
              </li>
              {suspiciousResult.policy_tag && (
                <li>
                  {isArabic ? 'وسم السياسة' : 'Policy tag'}:
                  {' '}<code>{suspiciousResult.policy_tag}</code>
                </li>
              )}
            </ul>
            <p style={{ marginTop: 10, fontSize: 12, color: 'var(--admin-text-dim)' }}>
              {isArabic
                ? 'تم مسح ad_ids المحذوفة من ذاكرة "المشاهدات الأخيرة" على هذا الجهاز. الصفحة الرئيسية و"المتجر" يجلبان من الخادم، لذا لن يظهر أي إعلان محذوف بعد التحديث. للحصول على أوضح حالة، حدّث الصفحة الرئيسية (Ctrl+F5 أو سحب للتحديث).'
                : 'Deleted ad_ids have been scrubbed from the on-device Recently-Viewed cache. Home and Shop fetch from the server, so deleted ads won’t reappear after refresh. For the cleanest state, hard-refresh Home (Ctrl+F5 / pull-to-refresh on mobile).'}
            </p>
          </div>
        )}

        {/* ────────────────────────────────────────────────────────────
            7. PRE-LAUNCH FULL ADS RESET (iter266)

            This is a DESTRUCTIVE tool: it deletes ALL ads. Kept in a
            dedicated, visually-distinct danger card with a bright
            red border so the operator can never confuse it with
            the seed/suspicious cleanup tools above.
        ──────────────────────────────────────────────────────────── */}
        <div
          className="admin-card"
          style={{ marginTop: 24, borderInlineStart: '4px solid #ef4444' }}
          data-testid="prelaunch-reset-card"
        >
          <h3 className="admin-card-title" style={{ color: '#ef4444' }}>
            <AlertTriangle size={16} style={{ verticalAlign: 'middle', marginInlineEnd: 6 }} />
            {isArabic ? 'إعادة تعيين كاملة لإعلانات ما قبل الإطلاق' : 'Pre-launch full ads reset'}
          </h3>
          <div
            style={{
              background: 'rgba(239, 68, 68, 0.08)',
              borderInlineStart: '3px solid #ef4444',
              padding: '10px 12px',
              borderRadius: 6,
              fontSize: 13,
              marginBottom: 10,
            }}
            data-testid="prelaunch-warning-banner"
          >
            {isArabic
              ? '⚠️ هذه الأداة تحذف كل الإعلانات الحالية ويجب استخدامها فقط قبل الإطلاق العام. لن تمس المستخدمين أو المسؤولين أو الرسائل أو المحفظة أو إعدادات النظام.'
              : '⚠️ This deletes ALL current ads and should only be used before public launch. It does NOT touch users, admins, messages, wallets, settings, or .env.'}
          </div>

          <button
            type="button"
            className="admin-btn"
            onClick={runPrelaunchDryRun}
            disabled={prelaunchDryRunLoading}
            data-testid="prelaunch-dry-run-btn"
          >
            <ListChecks size={14} />
            {prelaunchDryRunLoading
              ? (isArabic ? 'جارٍ الفحص…' : 'Running…')
              : (isArabic ? 'تشغيل فحص إعادة التعيين الكاملة' : 'Dry run full ads reset')}
          </button>
        </div>

        {/* Pre-launch dry-run result */}
        {prelaunchDryRun && (
          <div className="admin-card" data-testid="prelaunch-dryrun-result">
            <h4 className="admin-card-title">
              {isArabic ? 'نتائج فحص إعادة التعيين' : 'Pre-launch reset dry-run'}
              <span
                className="admin-pill red"
                style={{ marginInlineStart: 8, fontSize: 11 }}
              >
                {prelaunchDryRun.label}
              </span>
            </h4>

            <div className="admin-grid" style={{ gap: 12 }}>
              <div className="admin-stat">
                <span
                  className="admin-stat-value"
                  style={{ color: '#ef4444' }}
                  data-testid="prelaunch-total-ads"
                >
                  {prelaunchDryRun.total_ads}
                </span>
                <span className="admin-stat-label">
                  {isArabic ? 'سيتم حذفها' : 'Will be deleted'}
                </span>
              </div>
              <div className="admin-stat">
                <span
                  className="admin-stat-value"
                  style={{ color: '#22c55e' }}
                  data-testid="prelaunch-images-with"
                >
                  {prelaunchDryRun.images_breakdown?.ads_with_images ?? 0}
                </span>
                <span className="admin-stat-label">
                  {isArabic ? 'مع صور' : 'With images'}
                </span>
              </div>
              <div className="admin-stat">
                <span className="admin-stat-value" style={{ color: '#94a3b8' }}>
                  {prelaunchDryRun.images_breakdown?.ads_without_images ?? 0}
                </span>
                <span className="admin-stat-label">
                  {isArabic ? 'بدون صور' : 'Without images'}
                </span>
              </div>
              <div className="admin-stat">
                <span
                  className="admin-stat-value"
                  style={{ color: '#f59e0b' }}
                  data-testid="prelaunch-warnings-count"
                >
                  {prelaunchDryRun.warnings_count ?? 0}
                </span>
                <span className="admin-stat-label">
                  {isArabic ? 'تبدو كإعلانات حقيقية' : 'Look like real-user ads'}
                </span>
              </div>
            </div>

            {prelaunchDryRun.warnings_count > 0 && (
              <p
                style={{
                  marginTop: 10,
                  fontSize: 12,
                  color: '#f59e0b',
                }}
                data-testid="prelaunch-real-warning"
              >
                {isArabic
                  ? `⚠️ تنبيه: ${prelaunchDryRun.warnings_count} إعلان قد يكون إعلاناً حقيقياً (لا يطابق أي إشارة وهمية/تجريبية). راجع العينة أدناه قبل المتابعة.`
                  : `⚠️ Heads-up: ${prelaunchDryRun.warnings_count} ad${prelaunchDryRun.warnings_count === 1 ? '' : 's'} look like real-user posts (do not match any seed/suspicious heuristic). Review the sample below before proceeding.`}
              </p>
            )}

            {/* Status breakdown */}
            <h5 style={{ marginTop: 14, fontSize: 13 }}>
              {isArabic ? 'تفصيل الحالة' : 'Status breakdown'}
            </h5>
            <pre className="admin-pre" data-testid="prelaunch-status-breakdown">
{(prelaunchDryRun.status_breakdown || []).map(([k, v]) => `  ${String(v).padStart(4)}  ${k}`).join('\n') || '  (none)'}
            </pre>

            {/* Category breakdown */}
            <h5 style={{ marginTop: 10, fontSize: 13 }}>
              {isArabic ? 'تفصيل الفئة' : 'Category breakdown'}
            </h5>
            <pre className="admin-pre" data-testid="prelaunch-category-breakdown">
{(prelaunchDryRun.category_breakdown || []).slice(0, 20).map(([k, v]) => `  ${String(v).padStart(4)}  ${k}`).join('\n') || '  (none)'}
            </pre>

            {/* Owners top 20 */}
            <h5 style={{ marginTop: 10, fontSize: 13 }}>
              {isArabic ? 'أعلى المالكين (أعلى 20)' : 'Top owners (top 20)'}
            </h5>
            <pre className="admin-pre" data-testid="prelaunch-owners-breakdown">
{(prelaunchDryRun.owners_top20 || []).map(([uid, c]) => `  ${String(uid).padEnd(40)} ${String(c).padStart(4)} ads`).join('\n') || '  (none)'}
            </pre>

            {/* Sample 20 */}
            <h5 style={{ marginTop: 10, fontSize: 13 }}>
              {isArabic ? 'عينة من الإعلانات (20)' : 'Sample ads (20)'}
            </h5>
            <div style={{ overflowX: 'auto' }}>
              <table className="admin-table" data-testid="prelaunch-sample-table">
                <thead>
                  <tr>
                    <th>ad_id</th>
                    <th>{isArabic ? 'العنوان' : 'Title'}</th>
                    <th>{isArabic ? 'الفئة' : 'Category'}</th>
                    <th>{isArabic ? 'الحالة' : 'Status'}</th>
                    <th>{isArabic ? 'المالك' : 'Owner'}</th>
                    <th>{isArabic ? 'صور' : 'Images'}</th>
                    <th>{isArabic ? 'تاريخ الإنشاء' : 'Created'}</th>
                  </tr>
                </thead>
                <tbody>
                  {(prelaunchDryRun.sample_20_ads || []).map((s) => (
                    <tr key={s.ad_id}>
                      <td style={{ fontFamily: 'monospace', fontSize: 11 }}>{s.ad_id}</td>
                      <td>{s.title}</td>
                      <td>{s.category || '-'}</td>
                      <td>{s.status || '-'}</td>
                      <td style={{ fontFamily: 'monospace', fontSize: 11 }}>{s.user_id || '<none>'}</td>
                      <td>{s.has_images ? '✅' : '—'}</td>
                      <td style={{ fontSize: 11 }}>
                        {s.created_at ? String(s.created_at).slice(0, 19).replace('T', ' ') : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p style={{ marginTop: 10, fontSize: 12, color: 'var(--admin-text-dim)' }}>
              {isArabic
                ? `النسخة الاحتياطية ستُحفظ في المجموعة: ${prelaunchDryRun.backup_collection_on_execute}`
                : `Backup collection on execute: ${prelaunchDryRun.backup_collection_on_execute}`}
            </p>
          </div>
        )}

        {/* Pre-launch execute card */}
        {prelaunchDryRun && (
          <div
            className="admin-card"
            style={{ borderInlineStart: '4px solid #ef4444' }}
            data-testid="prelaunch-execute-card"
          >
            <h4 className="admin-card-title" style={{ color: '#ef4444' }}>
              <Trash2 size={14} style={{ verticalAlign: 'middle', marginInlineEnd: 6 }} />
              {isArabic
                ? 'تنفيذ إعادة التعيين الكاملة'
                : 'Execute full ads reset'}
            </h4>

            {prelaunchDryRun.total_ads === 0 ? (
              <p style={{ color: '#22c55e' }} data-testid="prelaunch-nothing-to-delete">
                {isArabic
                  ? '✓ لا توجد إعلانات لحذفها — المجموعة فارغة.'
                  : '✓ Nothing to delete — ads collection is already empty.'}
              </p>
            ) : (
              <>
                <p style={{ fontSize: 13 }}>
                  {isArabic
                    ? `سيتم حذف ${prelaunchDryRun.total_ads} إعلان نهائياً. سيتم حفظ نسخة احتياطية كاملة أولاً في ${prelaunchDryRun.backup_collection_on_execute}.`
                    : `${prelaunchDryRun.total_ads} ads will be permanently deleted. A full backup is written to ${prelaunchDryRun.backup_collection_on_execute} first.`}
                </p>
                <label style={{ display: 'block', fontSize: 12, marginTop: 8 }}>
                  {isArabic
                    ? `اكتب "${PRELAUNCH_RESET_CONFIRM}" للتأكيد:`
                    : `Type "${PRELAUNCH_RESET_CONFIRM}" to confirm:`}
                </label>
                <input
                  type="text"
                  value={prelaunchConfirmInput}
                  onChange={(e) => setPrelaunchConfirmInput(e.target.value)}
                  placeholder={PRELAUNCH_RESET_CONFIRM}
                  className="admin-input"
                  data-testid="prelaunch-confirm-input"
                  autoComplete="off"
                  spellCheck="false"
                  style={{ marginTop: 4 }}
                />
                <button
                  type="button"
                  className="admin-btn danger"
                  style={{ marginTop: 10 }}
                  onClick={runPrelaunchExecute}
                  disabled={!canPrelaunchExecute}
                  data-testid="prelaunch-execute-btn"
                >
                  {prelaunchExecuteLoading
                    ? (isArabic ? 'جارٍ الحذف…' : 'Resetting…')
                    : (isArabic
                        ? `حذف جميع الإعلانات قبل الإطلاق (${prelaunchDryRun.total_ads})`
                        : `Delete all ${prelaunchDryRun.total_ads} pre-launch ads`)}
                </button>
              </>
            )}
          </div>
        )}

        {/* Pre-launch result */}
        {prelaunchResult && (
          <div
            className="admin-card"
            data-testid="prelaunch-execute-result"
            style={{ borderInlineStart: '3px solid #22c55e' }}
          >
            <h4 className="admin-card-title" style={{ color: '#22c55e' }}>
              {isArabic ? 'تمت إعادة التعيين بنجاح' : 'Pre-launch reset complete'}
            </h4>
            <ul style={{ fontSize: 13, lineHeight: 1.8, margin: 0, paddingInlineStart: 18 }}>
              <li>
                {isArabic ? 'الإعلانات المحذوفة' : 'Deleted'}:
                {' '}<strong data-testid="prelaunch-result-deleted">{prelaunchResult.deleted_count}</strong>
              </li>
              <li>
                {isArabic ? 'إجمالي الإعلانات المتبقية' : 'Remaining ads'}:
                {' '}<strong data-testid="prelaunch-result-remaining">{prelaunchResult.remaining_ads_count}</strong>
              </li>
              {typeof prelaunchResult.users_count_unchanged === 'number' && (
                <li>
                  {isArabic ? 'عدد المستخدمين (لم يتغير)' : 'Users count (unchanged)'}:
                  {' '}<strong>{prelaunchResult.users_count_unchanged}</strong>
                </li>
              )}
              {typeof prelaunchResult.admins_count_unchanged === 'number' && (
                <li>
                  {isArabic ? 'عدد المسؤولين (لم يتغير)' : 'Admins count (unchanged)'}:
                  {' '}<strong>{prelaunchResult.admins_count_unchanged}</strong>
                </li>
              )}
              <li>
                {isArabic ? 'معرّف النسخة الاحتياطية' : 'Backup batch id'}:
                {' '}<code>{prelaunchResult.backup_batch_id}</code>
              </li>
              <li>
                {isArabic ? 'النسخة الاحتياطية محفوظة في' : 'Backup collection'}:
                {' '}<code>{prelaunchResult.backup_collection}</code>
              </li>
              <li>
                {isArabic ? 'المسؤول لا يزال مسجلاً' : 'Admin still logged in'}:
                {' '}<strong>{prelaunchResult.admin_still_logged_in ? '✅' : '❌'}</strong>
              </li>
            </ul>
            <p style={{ marginTop: 10, fontSize: 12, color: 'var(--admin-text-dim)' }}>
              {isArabic
                ? 'الصفحة الرئيسية و"المتجر" و"البحث" تجلب من الخادم، لذا ستظهر حالة فارغة فوراً بعد التحديث. سجل المشاهدات الأخيرة على هذا الجهاز يتحقق من الخادم — لن تظهر أي بطاقة مكسورة.'
                : 'Home, Shop, and Search fetch from the server — they will show empty states immediately on refresh. The Recently-Viewed strip validates against the server too, so no broken cards.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
